import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '@communityshare/ui-shared';
import getWatchLabels from '@/features/home-videos/utils/getWatchLabels';
import { videoShape } from './videoShape';
import useCardWatch from './useCardWatch';
import VideoThumbnail from './VideoThumbnail';
import DurationBadge from './DurationBadge';
import { PlayCircle } from './PlayIcons';
import { NewTabLink } from './TextLinks';

/**
 * Featured video card: thumbnail with play affordance and duration, title,
 * description, "Watch now" and (with a resource) a link to it labelled with
 * the resource's own title (its URL when the title is empty).
 *
 * vertical   - the default, used in the 2-video and 3-to-5-video layouts.
 * fillHeight - stretch to the row height (the 2-video layout, so the two
 *              cards' action rows line up).
 * horizontal - the single-video layout: thumbnail left, duration top-right,
 *              Watch now bottom-right on md+; stacked on mobile.
 */
FeaturedVideoCard.propTypes = {
  video: videoShape.isRequired,
  onWatch: PropTypes.func.isRequired,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  fillHeight: PropTypes.bool
};

export default function FeaturedVideoCard(props) {
  const { video, onWatch, orientation = 'vertical', fillHeight = false } = props;
  const { t } = useTranslation();
  const { watchRef, onCardClick } = useCardWatch(video, onWatch);
  const { duration, watchLabel } = getWatchLabels(video, t);
  const horizontal = orientation === 'horizontal';

  const text = (
    <div className={classNames('flex flex-col gap-3 min-w-0 text-cs-gray-700', horizontal ? 'pt-2.5 md:pt-2' : 'pt-3')}>
      <h3 className="font-semibold text-[17px] leading-6">{video.title}</h3>
      {video.description && (
        <p className="!mb-0 font-normal text-[15px] leading-[1.2]">{video.description}</p>
      )}
    </div>
  );

  const watchButton = (
    <div ref={watchRef} className="w-full md:w-auto">
      <ActionButton variant="primary" aria-label={watchLabel} className="whitespace-nowrap">
        {t('home_videos.card.watch_now')}
      </ActionButton>
    </div>
  );

  const resourceName = video.resource?.title || video.resource?.url;
  const guideLink = video.resource?.url ? (
    <NewTabLink
      href={video.resource.url}
      label={resourceName}
      ariaLabel={t('home_videos.card.resource_link_label', { resource: resourceName })}
      withIcon
      truncate
    />
  ) : null;

  const thumbnail = (
    <VideoThumbnail url={video.thumbnailUrl} className="group">
      <div className="absolute inset-0 flex items-center justify-center rounded-[10px] transition-colors duration-200 group-hover:bg-black/20">
        <PlayCircle size={horizontal ? 'md' : 'lg'} />
      </div>
      {!horizontal && duration && (
        <div className="absolute right-[13px] bottom-[13px]">
          <DurationBadge label={duration} variant="solid" />
        </div>
      )}
    </VideoThumbnail>
  );

  if (horizontal) {
    return (
      <div
        data-testid="video-card-featured"
        onClick={onCardClick}
        className={classNames(
          'w-full bg-cs-gray-100 rounded-[15px] p-3 cursor-pointer hover:shadow-md transition-shadow duration-200',
          'grid grid-cols-1 md:grid-cols-[183px_minmax(0,1fr)_auto] md:grid-rows-[auto_1fr] gap-x-2.5'
        )}
      >
        <div className="col-start-1 row-start-1 md:row-span-2 self-start">{thumbnail}</div>
        <div className="col-start-1 row-start-2 md:col-start-2 md:row-start-1">{text}</div>
        {duration && (
          <div className="col-start-1 row-start-3 mt-3 md:col-start-3 md:row-start-1 md:mt-0 self-start justify-self-start md:justify-self-end">
            <DurationBadge label={duration} />
          </div>
        )}
        {/* Spans the text and badge columns on md+ so a long resource title
            truncates here instead of squeezing the video's title. */}
        <div className="col-start-1 row-start-4 mt-3 md:col-start-2 md:col-span-2 md:row-start-2 self-end flex flex-col-reverse md:flex-row md:flex-wrap-reverse md:items-center gap-3 md:justify-end">
          {guideLink}
          {watchButton}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="video-card-featured"
      onClick={onCardClick}
      className={classNames(
        'w-full bg-cs-gray-100 rounded-[15px] p-3 flex flex-col cursor-pointer hover:shadow-md transition-shadow duration-200',
        fillHeight && 'h-full'
      )}
    >
      {thumbnail}
      {text}
      <div className="mt-auto pt-2.5 flex flex-col-reverse items-start gap-3 md:flex-row md:flex-wrap-reverse md:items-center md:justify-between">
        {guideLink}
        {watchButton}
      </div>
    </div>
  );
}
